import { useState, useMemo, useRef } from "react";
import Title from "../../../components/common/title/Title";
import { getApi } from "../../../services/api/globalApi";
import { digitsThree } from "../../../utils/price";
import UserReportsFilter from "./UserMounthlySalesMounthlyReportsFilter";
import ExportExcel from "../../../components/common/export-exel/ExportExel";
import Button from "../../../components/common/button/Button";
import ManualSalesAdjustmentModal from "./ManualSalesAdjustmentModal";
import { utilsState } from "../../../sotre/redux/features/utilsSlice";
import { useSelector } from "react-redux";
import { monthNamesFa, monthOrder } from "./monthConstants";
import useMonthlySalesCalculation from "./useMonthlySalesCalculation";

const MESSAGE_STYLES = {
  success: "bg-green-100 text-green-700",
  error: "bg-red-100 text-red-700",
  info: "bg-blue-100 text-blue-700",
};

function getReportErrorText(error) {
  if (error?.response?.status === 504 || error?.code === "ECONNABORTED") {
    return "پاسخ سرور بیش از حد طول کشید. فیلترها را محدودتر کنید (مثلاً یک کاربر یا نقش مشخص) و دوباره جستجو کنید.";
  }
  return "خطا در دریافت گزارش فروش ماهانه. دوباره تلاش کنید.";
}

function getYearFromKey(key) {
  const cleanKey = key.replace("NaKhales", "");
  const month = monthOrder.find((m) => cleanKey.startsWith(m));
  if (!month) return null;
  const suffix = cleanKey.slice(month.length);
  if (suffix.length === 4) return suffix;
  if (suffix.length === 3) return "1" + suffix;
  return null;
}

function getMonthFromKey(key) {
  const cleanKey = key.replace("NaKhales", "");
  return monthOrder.find((m) => cleanKey.startsWith(m)) || null;
}

export default function UsersMounthlySalesReports() {
  const [userReportsData, setUsersReportsData] = useState([]);
  const { utils } = useSelector(utilsState);
  const [activeSearchButton, setActiveSearchButton] = useState(true);
  const [pickedYear, setPickedYear] = useState(null);

  const availableYears = useMemo(() => {
    const years = new Set();
    userReportsData?.forEach((user) => {
      if (user.sales) {
        Object.keys(user.sales).forEach((key) => {
          if (!key.endsWith("NaKhales")) {
            const year = getYearFromKey(key);
            if (year) years.add(year);
          }
        });
      }
    });
    return Array.from(years).sort();
  }, [userReportsData]);

  const selectedYear = availableYears.includes(pickedYear)
    ? pickedYear
    : availableYears[availableYears.length - 1];

  const [reportError, setReportError] = useState(null);
  const latestReportRequest = useRef(0);

  const getReport = async (params) => {
    const path = "admin/reports/monthly-sales-formatted";
    const requestId = ++latestReportRequest.current;
    setActiveSearchButton(false);
    setReportError(null);
    try {
      const response = await getApi({ path, params: { ...params } });
      if (requestId !== latestReportRequest.current) return;
      setUsersReportsData(response?.data?.result || []);
    } catch (error) {
      if (requestId !== latestReportRequest.current) return;
      console.log(error);
      setUsersReportsData([]);
      setReportError(getReportErrorText(error));
    } finally {
      if (requestId === latestReportRequest.current) {
        setActiveSearchButton(true);
      }
    }
  };

  const [filter, setFilter] = useState();
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);

  const {
    start: startCalculation,
    isRunning: isCalculating,
    message: calcMessage,
    dismissMessage,
    percent: calcPercent,
    phaseLabel: calcPhaseLabel,
  } = useMonthlySalesCalculation({
    onFinished: () => {
      if (filter) getReport(filter);
    },
  });

  const handleCalculateMonthlySales = () => {
    if (!window.confirm("آیا مطمئنید؟ محاسبه فروش ماه قبل برای همه کاربران اجرا خواهد شد.")) return;
    startCalculation();
  };

  const filterDataHandler = (newParams) => {
    setFilter({ ...filter, ...newParams });
    const allFilter = { ...filter, ...newParams };
    getReport(allFilter);
  };

  const currentYearMonths = useMemo(() => {
    const months = [];
    userReportsData?.forEach((user) => {
      if (user.sales) {
        Object.keys(user.sales).forEach((key) => {
          if (!key.endsWith("NaKhales")) {
            const keyYear = getYearFromKey(key);
            if (keyYear === selectedYear && !months.includes(key)) {
              months.push(key);
            }
          }
        });
      }
    });
    return months.sort((a, b) => {
      return monthOrder.indexOf(getMonthFromKey(a)) - monthOrder.indexOf(getMonthFromKey(b));
    });
  }, [userReportsData, selectedYear]);

  const tableColumns = useMemo(() => {
    const baseColumns = [
      { title: "نام", value: "name" },
      { title: "موبایل", value: "mobile" },
    ];

    const monthColumns = [];
    currentYearMonths.forEach((monthKey) => {
      const baseMonth = getMonthFromKey(monthKey);
      const monthName = monthNamesFa[baseMonth] || baseMonth;
      monthColumns.push(
        { title: `${monthName} ناخالص`, value: `${monthKey}NaKhales` },
        { title: `${monthName} خالص`, value: monthKey }
      );
    });

    const currentMonthColumns = [
      { title: "ماه جاری ناخالص", value: "currentMonthGross" },
      { title: "ماه جاری خالص", value: "currentMonthNet" },
    ];

    return [...baseColumns, ...monthColumns, ...currentMonthColumns];
  }, [currentYearMonths]);

  const data = useMemo(() => {
    const mappedData = userReportsData?.map((item) => {
      const formattedSales = {};
      if (item.sales) {
        Object.keys(item.sales).forEach((key) => {
          formattedSales[key] = digitsThree(item.sales[key] || 0);
        });
      }
      return {
        ...item,
        ...formattedSales,
        currentMonthGross: digitsThree(item._ref_user_old_totalProductsPrice || 0),
        currentMonthNet: digitsThree(item._ref_user_finalPrice || 0),
        name: item?.name,
        mobile: item?.mobile,
        role: utils?.ROLES[item?.role]?.name || "",
        id: item._id,
      };
    }) || [];
    return mappedData;
  }, [userReportsData, utils]);


  const exportHeader = useMemo(() => {
    const monthHeaders = [];
    currentYearMonths.forEach((key) => {
      const baseMonth = getMonthFromKey(key);
      const monthName = monthNamesFa[baseMonth] || baseMonth;
      const year = getYearFromKey(key);
      monthHeaders.push(`${monthName} ${year} ناخالص`, `${monthName} ${year} خالص`);
    });
    return ["نام", "موبایل", "نقش", ...monthHeaders, "ماه جاری ناخالص", "ماه جاری خالص"];
  }, [currentYearMonths]);

  const exportReportsData = useMemo(() => {
    const mappedExport = userReportsData?.map((item) => {
      const exportRow = {
        نام: item?.name,
        موبایل: item?.mobile,
        نقش: utils.ROLES[item?.role]?.name || "",
      };
      currentYearMonths.forEach((key) => {
        const baseMonth = getMonthFromKey(key);
        const monthName = monthNamesFa[baseMonth] || baseMonth;
        const year = getYearFromKey(key);
        exportRow[`${monthName} ${year} ناخالص`] = item.sales?.[`${key}NaKhales`] || 0;
        exportRow[`${monthName} ${year} خالص`] = item.sales?.[key] || 0;
      });
      exportRow["ماه جاری ناخالص"] = item?._ref_user_old_totalProductsPrice || 0;
      exportRow["ماه جاری خالص"] = item?._ref_user_finalPrice || 0;
      return exportRow;
    }) || [];
    return mappedExport;
  }, [userReportsData, utils, currentYearMonths]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mb-4">
        <Title title={"گزارش فروش ماهانه"} />
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <Button
            onClick={() => setIsAdjustmentModalOpen(true)}
            text="افزودن اصلاحیه دستی"
            color="success"
            type="button"
            className="whitespace-nowrap"
          />
          <Button
            onClick={handleCalculateMonthlySales}
            disabled={isCalculating}
            text={
              isCalculating
                ? `در حال محاسبه...${calcPercent !== null ? ` ${calcPercent.toLocaleString("fa-IR")}٪` : ""}`
                : "محاسبه ماه قبل"
            }
            color="primary"
            type="button"
            className="whitespace-nowrap"
          />
          <ExportExcel jsonData={exportReportsData} header={exportHeader} />
        </div>
      </div>

      <ManualSalesAdjustmentModal
        isOpen={isAdjustmentModalOpen}
        onClose={() => setIsAdjustmentModalOpen(false)}
        onSuccess={() => {
          setIsAdjustmentModalOpen(false);
          if (filter) getReport(filter);
        }}
      />
      {isCalculating && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-blue-50 dark:bg-gray-800" role="status">
          <div className="flex justify-between text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
            <span>{calcPhaseLabel}</span>
            {calcPercent !== null && <span>{calcPercent.toLocaleString("fa-IR")}٪</span>}
          </div>
          <div className="w-full h-2 rounded-full bg-blue-100 dark:bg-gray-700 overflow-hidden">
            <div
              className={`h-2 rounded-full bg-blue-600 transition-all duration-500 ${calcPercent === null ? "w-1/3 animate-pulse" : ""}`}
              style={calcPercent === null ? undefined : { width: `${calcPercent}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            محاسبه در پس‌زمینه انجام می‌شود؛ می‌توانید در همین صفحه بمانید.
          </p>
        </div>
      )}
      {calcMessage && (
        <div
          className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium flex justify-between items-center ${MESSAGE_STYLES[calcMessage.type] || MESSAGE_STYLES.info}`}
          role="alert"
        >
          <span>{calcMessage.text}</span>
          <button type="button" onClick={dismissMessage} aria-label="بستن" className="text-lg leading-none px-2">
            ×
          </button>
        </div>
      )}

      <UserReportsFilter filterDataHandler={filterDataHandler} activeSearchButton={activeSearchButton} />

      {reportError && (
        <div className="mb-4 px-4 py-2 rounded-lg text-sm font-medium bg-red-100 text-red-700" role="alert">
          {reportError}
        </div>
      )}

      {data.length > 0 && (
        <div className="mt-6">
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
            {availableYears.map((year) => (
              <button
                key={year}
                onClick={() => setPickedYear(year)}
                className={`px-6 py-3 text-sm font-medium transition-all duration-200 border-b-2 -mb-px ${
                  selectedYear === year
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                سال {year}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full text-sm text-right text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 dark:text-gray-400">
                <tr>
                  {tableColumns.map((col, index) => (
                    <th key={index} scope="col" className="px-4 py-3 whitespace-nowrap font-semibold">
                      {col.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {tableColumns.map((col, colIndex) => (
                      <td key={colIndex} className="px-4 py-3 whitespace-nowrap">
                        {col.value === "name" ? (
                          <span className="font-medium text-gray-900 dark:text-white">{item[col.value]}</span>
                        ) : col.value === "mobile" ? (
                          <span className="text-gray-600 dark:text-gray-300 font-mono text-xs">{item[col.value]}</span>
                        ) : (
                          <span className={`${item[col.value] && item[col.value] !== "0" ? "text-green-600 dark:text-green-400 font-medium" : "text-gray-400"}`}>
                            {item[col.value] || "0"}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            نمایش {data.length} کاربر | سال {selectedYear} | {currentYearMonths.length} ماه
          </div>
        </div>
      )}

      {data.length === 0 && (
        <div className="mt-6 py-12 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
          <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-lg font-medium">هیچ دیتایی وجود ندارد</p>
          <p className="text-sm mt-1">لطفاً فیلترها را تنظیم کنید و جستجو کنید</p>
        </div>
      )}
    </div>
  );
}
